<template>
  <NuxtImg
    v-if="imageConfig && imageConfig.path"
    v-bind="{
      provider: imageConfig.provider,
      src: imageConfig.path,
      modifiers: imageModifiers,
      fit: crop,
      sizes: sanitizedSizes,

      alt,
      loading: imagePriority.isHighPriority ? 'eager' : loading,
      fetchpriority: imagePriority.isHighPriority ? 'high' : undefined,
      preload: imagePriority.isHighPriority,
    }"
    :style="focalPointStyle"
  />

  <img
    v-else-if="imageConfig && imageConfig.imageSizes"
    :src="imageConfig.imageSizes.src"
    :srcset="imageConfig.imageSizes.srcset"
    v-bind="{ alt }"
  />
</template>

<script>
export default defineNuxtComponent({
  props: {
    src: {
      type: String,
      required: true,
    },
    sizes: {
      type: String,
      default: null,
    },
    alt: {
      type: String,
      default: '',
    },
    loading: {
      type: String,
      default: 'lazy',
    },
    videoPreview: {
      type: Boolean,
      default: false,
    },

    // Modifiers
    ratio: {
      type: [Number, String],
      default: null,
    },
    quality: {
      type: [Number, String],
      default: 80,
    },
    crop: {
      type: String,
      default: 'cover',
    },
  },

  inject: {
    imagePriority: {
      from: 'imagePriority',
      default: () => ({ isHighPriority: false }),
    },
  },

  computed: {
    /**
     * Ensures the default (non-prefixed) vw size resolves to at least 1px
     * to prevent invalid 0w srcset descriptors.
     * Nuxt Image calculates the default size at viewport 0px, so Xvw * 0 = 0.
     * We replace it with the px equivalent at a 320px minimum viewport.
     */
    sanitizedSizes() {
      if (!this.sizes) return this.sizes;
      return this.sizes.replace(/^(\d+(?:\.\d+)?)vw\b/, (match, num) => {
        const minPx = Math.max(1, Math.ceil((320 * parseFloat(num)) / 100));
        return `${minPx}px`;
      });
    },

    /**
     * Always generate Cloudinary URL using existing $cldn plugin
     */
    imageConfig() {
      if (!this.src || typeof this.src !== 'string') {
        return false;
      }

      const imageConfig = this.$cldn.imgUrl(this.src);

      if (this.videoPreview) {
        const config = this.$cldn.imgUrl(this.src);
        const img = useImage();
        const imageSizes = img.getSizes(
          imageConfig.path,
          { sizes: this.sanitizedSizes, modifiers: this.imageModifiers },
          { provider: config.provider }
        );

        imageSizes.src = imageSizes.src.replace('image/upload', 'video/upload');
        imageSizes.srcset = imageSizes.srcset
          .split(',')
          .map((src) => src.replace('image/upload', 'video/upload'))
          .join(',');

        imageConfig.path = false;
        imageConfig.imageSizes = imageSizes;
      }

      return imageConfig;
    },

    /**
     * Build modifiers object for NuxtImg
     */
    focalPointStyle() {
      const focalPoint = this.$cldn.focalPoint(this.src);
      if (!focalPoint) return null;

      const amplify = 1.3;
      const x = Math.max(0, Math.min(100, 50 + (focalPoint.x - 50) * amplify));
      const y = Math.max(0, Math.min(100, 50 + (focalPoint.y - 50) * amplify));

      return { objectPosition: `${x}% ${y}%` };
    },

    imageModifiers() {
      const modifiers = {
        quality: this.quality,
      };

      const gravity = this.$cldn.extractGravityFromUrl(this.src);

      if (gravity) {
        modifiers.gravity = gravity;
      }

      if (this.ratio) {
        modifiers.aspectRatio = this.ratio;
      }

      if (this.crop) {
        modifiers.fit = this.crop;
      }

      if (this.videoPreview) {
        modifiers.e_preview = '1';
        modifiers.co_0 = '1';
        modifiers.format = 'jpg';
      }

      return modifiers;
    },
  },
});
</script>
